import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import {
  PedidoDTO,
  PedidoConsultaDTO,
  CancelarItemPedidoDTO,
  ItemDePedidoConsultaDTO,
  ItemDeLoteParaAdicionarDTO,
  LoteDeItensDePedidoDTO,
  VarianteDePrecoDeItemPedidoDTO,
  ModificadorDePrecoDeItemPedidoDTO,
} from '../../../../models/pedidos/pedido.model';
import { ProdutoEscolhidoNaGrade } from '../pedido-adicionar-item-dialog/pedido-adicionar-item-dialog.component';
import { TableColumn } from '../../../../models/shared/table-column.model';
import { PedidoService } from '../../../../services/pedidos/pedido.service';
import { MessageService } from 'primeng/api';
import { PermissaoService } from '../../../../services/permissao/permissao.service';
import { MoedaConfig } from '../../../../enums/moeda.enum';
import { EstadoDePedido, ESTADO_PEDIDO_CONFIG } from '../../../../enums/EstadoDePedido.enum';
import {
  ACAO_ENTREGAR,
  ACAO_ENTREGAR_E_FINALIZAR,
  calcularEstadosDisponiveis,
} from '../pedido-estados-dialog/pedido-estados-dialog.component';
import { itemFoiCancelado, itemFoiFaturado, pedidoEstaEncerrado } from '../../utils/pedido-item-status.util';

/**
 * Item que o operador escolheu mas ainda NAO confirmou. Vira uma linha nova do
 * pedido quando o lote for enviado.
 */
export interface PendenciaDeAdicaoDeItem {
  /** Agrupa cliques repetidos na mesma origem numa pendencia so. */
  chave: string;
  itemId: number;
  /** Codigo do cadastro, para a linha pendente preencher a coluna COD. */
  codigoItem?: number;
  /** Codigo de barras, idem, para a coluna opcional. */
  barras?: string;
  nome: string;
  valorUnitario: number;
  quantidade: number;
  /** Duplicar nunca pode somar na linha antiga: exige linha nova. */
  forcarNovaLinha: boolean;
  observacao?: string;
  variantesDePreco?: VarianteDePrecoDeItemPedidoDTO[];
  modificadores?: ModificadorDePrecoDeItemPedidoDTO[];
}

@Component({
  selector: 'app-pedido-detalhe-dialog',
  templateUrl: './pedido-detalhe-dialog.component.html',
  styleUrls: ['./pedido-detalhe-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PedidoDetalheDialogComponent implements OnDestroy {

  @Input() visivel = false;
  @Input() pedido: PedidoDTO | null = null;
  @Input() loading = false;
  @Input() texts: any = {};

  // Configuracao de moeda da empresa (propagada pelo componente pai).
  // Defaults Real (BRL) protegem contra render antes da chegada da sessao.
  @Input() moedaPadrao: 'BRL' | 'PYG' | 'USD' | 'ARS' = 'BRL';
  @Input() localeMoeda = 'pt-BR';
  @Input() digitsMoeda = '1.2-2';

  @Output() visivelChange = new EventEmitter<boolean>();
  @Output() mudarEstado = new EventEmitter<{ pedido: PedidoDTO; acao: string }>();
  @Output() cancelarPedido = new EventEmitter<PedidoDTO>();
  @Output() cancelarItem = new EventEmitter<CancelarItemPedidoDTO>();
  @Output() abrirWhatsApp = new EventEmitter<PedidoDTO>();
  @Output() compartilharPedido = new EventEmitter<PedidoDTO>();
  @Output() faturar = new EventEmitter<{ pedido: PedidoDTO; itens: ItemDePedidoConsultaDTO[] }>();
  @Output() finalizarPedido = new EventEmitter<PedidoDTO>();
  @Output() entregarPedido = new EventEmitter<PedidoDTO>();
  @Output() entregarEFinalizarPedido = new EventEmitter<PedidoDTO>();
  @Output() abrirNota = new EventEmitter<number>();
  @Output() imprimirPedido = new EventEmitter<PedidoDTO>();
  // Persistir alteracao de quantidade do item (debounced no componente).
  @Output() alterarQuantidadeItem = new EventEmitter<{ itemDePedidoId: number; quantidade: number }>();
  // Pedido atualizado apos adicionar item pela grade de produtos.
  @Output() pedidoAtualizado = new EventEmitter<PedidoConsultaDTO>();

  // Dialog estados
  dialogEstadosVisivel = false;

  // Dialog de adicionar item (grade de produtos)
  adicionarItemVisivel = false;

  // Timers de debounce para persistir a quantidade por item.
  private persistTimers = new Map<number, any>();

  // Dialog cancelar item
  dialogCancelarItemVisivel = false;
  itemParaCancelar: ItemDePedidoConsultaDTO | null = null;
  quantidadeCancelar = 1;

  // ============================================================
  // Alteracoes pendentes (modelo staged)
  //
  // Adicionar item a item disparava um POST por clique — e o backend
  // imprime UMA comanda complementar por POST. Agora as escolhas ficam paradas
  // aqui na tela ate o operador confirmar, e vao juntas num unico lote.
  //
  // O estado mora FORA do objeto `pedido` de proposito: o pai troca a
  // referencia do pedido a cada auto-refresh (30s), e pendencias guardadas
  // dentro dele evaporariam no meio da edicao. Na tela elas entram como
  // sobreposicao (ver `saldoExibido`, `quantidadeExibida`).
  // ============================================================

  // ============================================================
  // Colunas da tabela de itens
  //
  // Mesmo padrao das listagens (app-column-selector + localStorage). Ficam
  // FIXAS as colunas sem as quais a linha deixa de ser conferivel — item,
  // quantidade, saldo, valores e o botao de remover; o codigo do item e
  // opcional, porque so interessa a quem confere por codigo.
  // ============================================================

  readonly STORAGE_KEY_COLUNAS = 'pedido-detalhe-itens-visible-columns';

  colunasItens: TableColumn[] = [
    { key: 'codigo', labelKey: 'pedidos.labels.codigo', visible: false, fixed: false, defaultVisible: false },
    { key: 'barras', labelKey: 'pedidos.labels.codigo_de_barras', visible: false, fixed: false, defaultVisible: false },
    { key: 'item', labelKey: 'pedidos.labels.item', visible: true, fixed: true, defaultVisible: true },
    { key: 'quantidade', labelKey: 'pedidos.labels.quantidade', visible: true, fixed: true, defaultVisible: true },
    { key: 'saldo', labelKey: 'pedidos.labels.saldo', visible: true, fixed: true, defaultVisible: true },
    { key: 'valorUnitario', labelKey: 'pedidos.labels.valor_unitario', visible: true, fixed: true, defaultVisible: true },
    { key: 'valorTotal', labelKey: 'pedidos.labels.valor_total', visible: true, fixed: true, defaultVisible: true },
    { key: 'acoes', labelKey: 'pedidos.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  /** Unidades a mais numa linha JA existente, por id do item de pedido. */
  private incrementosPendentes = new Map<number, number>();

  /** Itens novos ainda nao confirmados. */
  pendenciasDeAdicao: PendenciaDeAdicaoDeItem[] = [];

  /** Espelho de `pendenciasDeAdicao` por item de catalogo, para o badge da grade. */
  pendenciasPorCatalogo: Record<number, number> = {};

  /** True enquanto o lote esta sendo enviado. */
  confirmandoAlteracoes = false;

  /** Pergunta "descartar alteracoes?" ao tentar fechar com pendencias. */
  dialogDescartarVisivel = false;

  /** Sequencia para dar chave unica a pendencias que nao devem coalescer. */
  private sequenciaDePendencia = 0;

  constructor(
    private cdr: ChangeDetectorRef,
    private pedidoService: PedidoService,
    private messageService: MessageService,
    private permissaoService: PermissaoService,
  ) {}

  /**
   * Operar o pedido exige GESTAO_DE_PEDIDOS. O detalhe abre para quem so tem
   * VISUALIZAR_PEDIDOS, entao a checagem fica aqui e nao na entrada da tela.
   * Mesma divisao do mobile (pedidos_dropdown.dart).
   */
  get podeOperarPedido(): boolean {
    return this.permissaoService.temPermissao('gestaoDePedidos');
  }

  /** Config de moeda para o widget de busca formatar o preço nas sugestões. */
  get cfgMoedaBusca(): MoedaConfig {
    return {
      symbol: this.moedaPadrao,
      digits: this.digitsMoeda,
      locale: this.localeMoeda,
      label: this.moedaPadrao,
    };
  }

  /**
   * Item escolhido no widget de busca inline (`app-operar-venda-item-busca`):
   * vira pendencia de adicao. Cliques repetidos no mesmo produto (sem
   * personalizacao) somam na MESMA pendencia.
   */
  onItemBuscaEscolhido(produto: any): void {
    if (!produto?.id) return;
    this.acumularPendenciaDeAdicao({
      chave: `catalogo:${produto.id}`,
      itemId: produto.id,
      codigoItem: produto.codigoItem,
      barras: produto.barras,
      nome: produto.nome || produto.descricao || '-',
      valorUnitario: Number(produto.precoDeVenda ?? 0),
      quantidade: 1,
      forcarNovaLinha: false,
    });
  }

  /**
   * Item escolhido com multiplicador ("3*401"): entra com a quantidade pedida,
   * LIMITADA AO SALDO. Pedir 5 de um item com 2 em estoque adiciona 2 e avisa;
   * item sem saldo nao entra.
   *
   * O teto considera o que ja esta pendente do MESMO item de catalogo, senao
   * repetir "3*401" tres vezes furaria o estoque uma unidade por vez.
   */
  onItemBuscaEscolhidoComQuantidade(escolha: { produto: any; quantidade: number }): void {
    const produto = escolha?.produto;
    if (!produto?.id) return;

    const saldo = Number(produto.saldo ?? 0);
    const jaPendente = this.pendenciasPorCatalogo[produto.id] || 0;
    const espaco = Math.max(saldo - jaPendente, 0);

    if (espaco <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.texts?.mensagens?.sem_saldo_para_adicionar || 'Sin saldo disponible',
      });
      return;
    }

    const pedida = Math.max(Number(escolha.quantidade) || 1, 1);
    const efetiva = Math.min(pedida, espaco);

    this.acumularPendenciaDeAdicao({
      chave: `catalogo:${produto.id}`,
      itemId: produto.id,
      codigoItem: produto.codigoItem,
      barras: produto.barras,
      nome: produto.nome || produto.descricao || '-',
      valorUnitario: Number(produto.precoDeVenda ?? 0),
      quantidade: efetiva,
      forcarNovaLinha: false,
    });

    if (efetiva < pedida) {
      this.messageService.add({
        severity: 'warn',
        summary: (this.texts?.mensagens?.limite_saldo || '').replace('{{saldo}}', String(saldo))
          || `Saldo: ${saldo}`,
      });
    }
  }

  /** Produto escolhido na grade completa — mesmo tratamento da busca inline. */
  onProdutoEscolhidoNaGrade(escolha: ProdutoEscolhidoNaGrade): void {
    this.acumularPendenciaDeAdicao({
      chave: `catalogo:${escolha.itemId}`,
      itemId: escolha.itemId,
      codigoItem: escolha.codigoItem,
      barras: escolha.barras,
      nome: escolha.nome,
      valorUnitario: escolha.valorUnitario,
      quantidade: 1,
      forcarNovaLinha: false,
    });
  }

  /**
   * Soma na pendencia de mesma chave ou cria uma nova. Coalescer e o que evita
   * a lista virar uma pilha de "1x cafe" repetidos.
   */
  private acumularPendenciaDeAdicao(nova: PendenciaDeAdicaoDeItem): void {
    const existente = this.pendenciasDeAdicao.find(p => p.chave === nova.chave);
    if (existente) {
      existente.quantidade += nova.quantidade;
    } else {
      this.pendenciasDeAdicao = [...this.pendenciasDeAdicao, nova];
    }
    this.recomputarPendenciasPorCatalogo();
    this.cdr.markForCheck();
  }

  /** Remove uma pendencia de adicao inteira (o "x" da linha pendente). */
  removerPendenciaDeAdicao(pendencia: PendenciaDeAdicaoDeItem): void {
    this.pendenciasDeAdicao = this.pendenciasDeAdicao.filter(p => p !== pendencia);
    this.recomputarPendenciasPorCatalogo();
    this.cdr.markForCheck();
  }

  /** Uma unidade a mais na pendencia (o "+" da linha pendente). */
  acumularUmaUnidadeNaPendencia(pendencia: PendenciaDeAdicaoDeItem): void {
    pendencia.quantidade += 1;
    this.recomputarPendenciasPorCatalogo();
    this.cdr.markForCheck();
  }

  /** Uma unidade a menos numa pendencia; some quando zera. */
  removerUmaUnidadeDaPendencia(pendencia: PendenciaDeAdicaoDeItem): void {
    if (pendencia.quantidade <= 1) {
      this.removerPendenciaDeAdicao(pendencia);
      return;
    }
    pendencia.quantidade -= 1;
    this.recomputarPendenciasPorCatalogo();
    this.cdr.markForCheck();
  }

  private recomputarPendenciasPorCatalogo(): void {
    const mapa: Record<number, number> = {};
    this.pendenciasDeAdicao.forEach(p => {
      mapa[p.itemId] = (mapa[p.itemId] || 0) + p.quantidade;
    });
    this.pendenciasPorCatalogo = mapa;
  }

  // ============================================================
  // Por que a linha esta apagada
  //
  // Linha com saldo 0 aparece esmaecida, sem dizer o motivo. O fundo colorido
  // completa a informacao: verde = faturada, vermelho = cancelada. A regra vive
  // em pedido-item-status.util porque o card da listagem pinta as mesmas linhas
  // com o mesmo criterio — as duas telas tem que andar juntas.
  // ============================================================

  /** Linha esgotada cuja quantidade TODA foi cancelada. */
  linhaCancelada(item: ItemDePedidoConsultaDTO): boolean {
    return itemFoiCancelado(item);
  }

  /** Linha esgotada com algum faturamento (mista conta como faturada). */
  linhaFaturada(item: ItemDePedidoConsultaDTO): boolean {
    return itemFoiFaturado(item);
  }

  // ============================================================
  // Duplicar item ja faturado
  //
  // O "+" mexeria na linha que ja virou nota; duplicar pede uma unidade NOVA do
  // mesmo produto, com a mesma personalizacao. Entra como pendencia com
  // `forcarNovaLinha`, para o backend nao somar de volta na linha antiga.
  // Cliques repetidos no mesmo botao somam na MESMA pendencia — nada de uma
  // fila de linhas fantasma.
  // ============================================================

  /** True quando a linha ja tem faturamento e da para montar o corpo (itemId nao-nulo). */
  podeDuplicarItem(item: ItemDePedidoConsultaDTO): boolean {
    return (item.quantidadeFaturada || 0) > 0 && item.item?.id != null;
  }

  /** Pendencia de 1 unidade do mesmo produto, com a personalizacao da origem. */
  duplicarItem(item: ItemDePedidoConsultaDTO): void {
    if (item.item?.id == null) return;
    // A chave usa a LINHA de origem: duplicar duas linhas diferentes do mesmo
    // produto gera duas pendencias, como o operador espera.
    const chave = item.id != null
      ? `duplicar:${item.id}`
      : `duplicar:novo:${++this.sequenciaDePendencia}`;
    this.acumularPendenciaDeAdicao({
      chave,
      itemId: item.item.id,
      nome: item.item?.nome || item.produtoCardapioNome || '-',
      valorUnitario: item.valorUnitario,
      quantidade: 1,
      forcarNovaLinha: true,
      observacao: item.observacao,
      variantesDePreco: item.variantesDePreco,
      modificadores: item.modificadores,
    });
  }

  // ============================================================
  // Confirmar / descartar o lote
  // ============================================================

  temPendencias(): boolean {
    return this.pendenciasDeAdicao.length > 0 || this.incrementosPendentes.size > 0;
  }

  /** Quantas unidades estao esperando confirmacao (rotulo do botao). */
  totalDeUnidadesPendentes(): number {
    let total = 0;
    this.pendenciasDeAdicao.forEach(p => total += p.quantidade);
    this.incrementosPendentes.forEach(delta => total += delta);
    return total;
  }

  /**
   * Enquanto ha pendencias as acoes que trocam de tela ou mudam o estado do
   * pedido ficam bloqueadas: Faturar navega para outra rota e levaria as
   * pendencias embora sem avisar.
   */
  acoesBloqueadasPorPendencias(): boolean {
    return this.temPendencias();
  }

  /** Monta e envia o lote. Em caso de erro as pendencias ficam onde estao. */
  confirmarAlteracoes(): void {
    if (!this.pedido?.id || !this.temPendencias() || this.confirmandoAlteracoes) return;

    const adicionar: ItemDeLoteParaAdicionarDTO[] = this.pendenciasDeAdicao.map(p => ({
      itemId: p.itemId,
      quantidade: p.quantidade,
      valorUnitario: p.valorUnitario,
      valorTotal: p.valorUnitario * p.quantidade,
      saldo: p.quantidade,
      quantidadeCancelada: 0,
      quantidadeFaturada: 0,
      forcarNovaLinha: p.forcarNovaLinha,
      ...(p.observacao ? { observacao: p.observacao } : {}),
      ...(p.variantesDePreco?.length ? { variantesDePreco: p.variantesDePreco } : {}),
      ...(p.modificadores?.length ? { modificadores: p.modificadores } : {}),
    }));

    // `quantidade` e o TOTAL novo da linha, nao o incremento.
    const alterarQuantidade = Array.from(this.incrementosPendentes.entries())
      .map(([itemDePedidoId, delta]) => {
        const linha = this.pedido?.itens?.find(i => i.id === itemDePedidoId);
        if (!linha) return null;
        return { itemDePedidoId, quantidade: (linha.quantidade || 0) + delta };
      })
      .filter((a): a is { itemDePedidoId: number; quantidade: number } => a !== null);

    const lote: LoteDeItensDePedidoDTO = {};
    if (adicionar.length) lote.adicionar = adicionar;
    if (alterarQuantidade.length) lote.alterarQuantidade = alterarQuantidade;
    if (!lote.adicionar && !lote.alterarQuantidade) return;

    this.confirmandoAlteracoes = true;
    this.cdr.markForCheck();

    this.pedidoService.alterarItensEmLote(this.pedido.id, lote).subscribe({
      next: (pedido) => {
        this.confirmandoAlteracoes = false;
        this.limparPendencias();
        this.messageService.add({
          severity: 'success',
          summary: this.texts?.mensagens?.sucesso_confirmar_alteracoes
            || 'Alteraciones confirmadas',
        });
        this.pedidoAtualizado.emit(pedido);
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.confirmandoAlteracoes = false;
        this.messageService.add({
          severity: 'error',
          summary: err?.error?.message
            || this.texts?.mensagens?.erro_confirmar_alteracoes
            || 'No se pudieron confirmar las alteraciones.',
        });
        this.cdr.markForCheck();
      },
    });
  }

  /** Joga fora tudo o que nao foi confirmado. */
  descartarAlteracoes(): void {
    this.limparPendencias();
    this.cdr.markForCheck();
  }

  private limparPendencias(): void {
    this.pendenciasDeAdicao = [];
    this.incrementosPendentes.clear();
    this.pendenciasPorCatalogo = {};
  }

  // ============================================================
  // Projecao na tela (pendencias sobrepostas ao pedido do servidor)
  // ============================================================

  /** Unidades pendentes desta linha. */
  incrementoPendente(item: ItemDePedidoConsultaDTO): number {
    return item.id != null ? (this.incrementosPendentes.get(item.id) || 0) : 0;
  }

  temIncrementoPendente(item: ItemDePedidoConsultaDTO): boolean {
    return this.incrementoPendente(item) > 0;
  }

  saldoExibido(item: ItemDePedidoConsultaDTO): number {
    return (item.saldo || 0) + this.incrementoPendente(item);
  }

  quantidadeExibida(item: ItemDePedidoConsultaDTO): number {
    return (item.quantidade || 0) + this.incrementoPendente(item);
  }

  valorTotalExibido(item: ItemDePedidoConsultaDTO): number {
    return this.quantidadeExibida(item) * (item.valorUnitario || 0);
  }

  /** Valor total do pedido somando o que ainda nao foi confirmado. */
  valorTotalProjetado(): number {
    const base = this.pedido?.valorTotal || 0;
    let extra = 0;
    (this.pedido?.itens || []).forEach(item => {
      extra += this.incrementoPendente(item) * (item.valorUnitario || 0);
    });
    this.pendenciasDeAdicao.forEach(p => extra += p.quantidade * p.valorUnitario);
    return base + extra;
  }

  ngOnDestroy(): void {
    this.persistTimers.forEach(t => clearTimeout(t));
    this.persistTimers.clear();
  }

  /**
   * Fechar com alteracoes pendentes PERGUNTA antes: sair calado jogaria fora o
   * que o operador acabou de montar. Com pendencias o dialogo tambem fica
   * `closable: false`, entao Escape e clique fora nao escapam da pergunta —
   * as duas saidas (X do header e "Fechar" do rodape) passam por aqui.
   */
  onHide(): void {
    if (this.temPendencias()) {
      this.dialogDescartarVisivel = true;
      this.cdr.markForCheck();
      return;
    }
    this.fecharDialogo();
  }

  /** Confirmou o descarte na pergunta: joga fora as pendencias e fecha. */
  confirmarDescarteEFechar(): void {
    this.dialogDescartarVisivel = false;
    this.limparPendencias();
    this.fecharDialogo();
  }

  private fecharDialogo(): void {
    this.persistTimers.forEach(t => clearTimeout(t));
    this.persistTimers.clear();
    this.visivelChange.emit(false);
  }

  /**
   * Resolve o estado do pedido para string.
   * O backend serializa enums como objeto { enum: "ABERTO", nome: "Aberto", ... }
   */
  /** Pedido sem linha ativa (FINALIZADO/CANCELADO): as linhas liquidadas nao esmaecem. */
  pedidoEncerrado(): boolean {
    return pedidoEstaEncerrado(this.pedido ? this.resolveEstado(this.pedido.estadoDePedido) : null);
  }

  resolveEstado(estado: any): EstadoDePedido {
    if (typeof estado === 'string') return estado as EstadoDePedido;
    if (estado?.enum) return estado.enum as EstadoDePedido;
    if (estado?.name) return estado.name as EstadoDePedido;
    return EstadoDePedido.ABERTO;
  }

  getCorEstado(estado: any): string {
    return ESTADO_PEDIDO_CONFIG[this.resolveEstado(estado)]?.cor || '#999';
  }

  getBackgroundEstado(estado: any): string {
    const config = ESTADO_PEDIDO_CONFIG[this.resolveEstado(estado)];
    return config?.gradiente || config?.cor || '#999';
  }

  getIconeEstado(estado: any): string {
    return ESTADO_PEDIDO_CONFIG[this.resolveEstado(estado)]?.icone || 'pi pi-circle';
  }

  getEstadoLabel(estado: any): string {
    const resolved = this.resolveEstado(estado);
    const label = this.texts?.estados?.[resolved];
    if (label && typeof label === 'string') return label;
    if (typeof estado === 'object' && estado?.nome) return estado.nome;
    return resolved;
  }

  /** Retorna true quando o pedido e do tipo DELIVERY. */
  ehDelivery(): boolean {
    const raw = this.pedido?.tipoEntrega;
    const tipo = typeof raw === 'object' && raw !== null
      ? (raw as any).enum || (raw as any).name
      : raw;
    return tipo === 'DELIVERY';
  }

  formatarDataExibicao(dataStr?: string): string {
    if (!dataStr) return '-';
    try {
      const data = new Date(dataStr);
      return data.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dataStr;
    }
  }

  // ============================================================
  // Regras de negocio - visibilidade de acoes
  // ============================================================

  /**
   * Venda fechada: delivery pago online (QR Code ou link do tpago confirmado) e já faturado.
   *
   * Não recebe item novo nem pode ser faturada de novo — o backend recusa as duas coisas, e a
   * tela apenas não oferece o que seria recusado. A conta de quando isso vale é do backend
   * (`Pedido.ehVendaFechadaPorPagamentoOnline`): comanda de balcão, que é faturada em partes,
   * fica de fora mesmo com pagamento adiantado.
   */
  get pedidoPagoAntecipadamente(): boolean {
    return this.pedido?.vendaFechadaPorPagamentoOnline === true;
  }

  botoesDeAcaoVisiveis(): boolean {
    if (!this.pedido || !this.podeOperarPedido) return false;
    if (this.pedidoPagoAntecipadamente) return false;
    const estado = this.resolveEstado(this.pedido.estadoDePedido);
    return estado !== EstadoDePedido.CANCELADO
        && estado !== EstadoDePedido.FINALIZADO;
  }

  podeCancelarItem(): boolean {
    if (!this.pedido || !this.podeOperarPedido) return false;
    const estado = this.resolveEstado(this.pedido.estadoDePedido);
    return estado !== EstadoDePedido.CANCELADO
        && estado !== EstadoDePedido.ENTREGUE;
  }

  // ============================================================
  // Estados disponiveis para transicao
  // ============================================================
  // A regra de quais transicoes existem vive em `calcularEstadosDisponiveis`
  // (pedido-estados-dialog), compartilhada com o card da listagem.

  /** Usado so para decidir se o botao "Estados" aparece no rodape. */
  temEstadosDisponiveis(): boolean {
    if (!this.podeOperarPedido) return false;
    return calcularEstadosDisponiveis(this.pedido, this.texts).length > 0;
  }

  /**
   * Ajuste de quantidade (+/-) na coluna Saldo. Espelha a guarda do backend
   * (`PedidoService.validarPedidoAceitaAlteracaoDeItens`), que so recusa FINALIZADO e CANCELADO —
   * ou seja, ABERTO tambem edita.
   *
   * Existe separado de `podeFaturar()` porque aquele getter responde "da para emitir nota", nao
   * "da para editar": ele exclui ABERTO, e o adjuster pendurado nele deixava o pedido aberto sem
   * como mudar a quantidade, mesmo com a API aceitando.
   */
  podeAjustarQuantidade(): boolean {
    if (!this.pedido || !this.podeOperarPedido) return false;
    if (this.pedidoPagoAntecipadamente) return false;
    const estado = this.resolveEstado(this.pedido.estadoDePedido);
    if (estado === EstadoDePedido.CANCELADO || estado === EstadoDePedido.FINALIZADO) return false;
    return this.pedido.itens.some(i => (i.saldo || 0) > 0);
  }

  // Pode faturar: qualquer estado em andamento (inclusive ABERTO) com itens com saldo > 0.
  // O backend nunca barrou por estado — so por saldo.
  podeFaturar(): boolean {
    if (!this.pedido || !this.podeOperarPedido) return false;
    if (this.pedidoPagoAntecipadamente) return false;
    const estado = this.resolveEstado(this.pedido.estadoDePedido);
    const estadoPermitido = estado === EstadoDePedido.ABERTO
        || estado === EstadoDePedido.EM_PRODUCAO
        || estado === EstadoDePedido.A_CAMINHO
        || estado === EstadoDePedido.AGUARDANDO_RETIRADA
        || estado === EstadoDePedido.ENTREGUE;
    if (!estadoPermitido) return false;
    // Verificar se ha itens com saldo disponivel
    return this.pedido.itens.some(i => (i.saldo || 0) > 0);
  }

  // Pode finalizar: sem saldo pendente e num estado que permita a acao.
  // Alem de ENTREGUE, aceita EM_PRODUCAO quando NAO e delivery — o pedido de
  // balcao totalmente faturado ficava sem saida (nao da para faturar, nao e
  // ENTREGUE, e cancelar e recusado por ja ter nota). O dominio ja aceitava:
  // `Pedido.finalizar()` so exige saldo zero. Espelha `temBotaoFinalizar()` do
  // card — as duas regras precisam andar juntas.
  podeFinalizar(): boolean {
    if (!this.pedido || !this.podeOperarPedido) return false;
    const estado = this.resolveEstado(this.pedido.estadoDePedido);
    const estadoPermite =
      estado === EstadoDePedido.ENTREGUE ||
      (estado === EstadoDePedido.EM_PRODUCAO && !this.ehDelivery());
    if (!estadoPermite) return false;
    return this.pedido.itens.every(i => (i.saldo ?? 0) === 0);
  }

  // Pode entregar: A_CAMINHO ou AGUARDANDO_RETIRADA
  podeEntregar(): boolean {
    if (!this.pedido) return false;
    const estado = this.resolveEstado(this.pedido.estadoDePedido);
    return estado === EstadoDePedido.A_CAMINHO
        || estado === EstadoDePedido.AGUARDANDO_RETIRADA;
  }

  onFinalizar(): void {
    if (!this.pedido) return;
    this.finalizarPedido.emit(this.pedido);
  }

  onEntregar(): void {
    if (!this.pedido) return;
    this.entregarPedido.emit(this.pedido);
  }

  onEntregarEFinalizar(): void {
    if (!this.pedido) return;
    this.entregarEFinalizarPedido.emit(this.pedido);
  }

  onNotaClick(notaId: number): void {
    this.abrirNota.emit(notaId);
  }

  /**
   * Emite o evento de impressao do pedido.
   * O componente pai fecha o modal e navega para a rota dedicada
   * /pedidos/imprimir/:id passando o PedidoDTO via router state.
   */
  onImprimirPedidoClick(): void {
    if (!this.pedido) return;
    this.imprimirPedido.emit(this.pedido);
  }

  // ============================================================
  // Acoes
  // ============================================================

  onMudarEstado(acao: string): void {
    if (!this.pedido) return;
    this.mudarEstado.emit({ pedido: this.pedido, acao });
  }

  /** Roteia a opcao escolhida no seletor de estados para a acao correspondente. */
  onAcaoEstadoEscolhida(estado: { acao: string }): void {
    if (estado.acao === ACAO_ENTREGAR) {
      this.onEntregar();
      return;
    }
    if (estado.acao === ACAO_ENTREGAR_E_FINALIZAR) {
      this.onEntregarEFinalizar();
      return;
    }
    if (estado.acao === 'cancelar') {
      this.onCancelarPedido();
      return;
    }
    this.onMudarEstado(estado.acao);
  }

  onFaturar(): void {
    if (!this.pedido) return;
    const itensParaFaturar = this.pedido.itens.filter(i => i.saldo > 0);
    if (itensParaFaturar.length === 0) return;
    this.faturar.emit({ pedido: this.pedido, itens: itensParaFaturar });
  }

  // ============================================================
  // Ajuste de quantidade (+/-)
  //
  // O "+" e STAGED: acumula aqui e so vai para o servidor no lote confirmado,
  // porque aumentar quantidade tambem manda item para a cozinha.
  //
  // O "-" continua IMEDIATO (otimista + debounce + PATCH): tirar item nao
  // imprime nada e o operador espera efeito na hora. A unica excecao e quando
  // a linha tem incremento pendente — ai o "-" desfaz a pendencia local, sem
  // tocar na rede; so abaixo da quantidade ja confirmada ele volta a persistir.
  // ============================================================

  addQuantidade(item: ItemDePedidoConsultaDTO): void {
    if (item.id == null) return;
    this.incrementosPendentes.set(item.id, this.incrementoPendente(item) + 1);
    this.cdr.markForCheck();
  }

  /** O "-" para de fazer sentido quando restaria menos de 1 unidade. */
  podeRemoverQuantidade(item: ItemDePedidoConsultaDTO): boolean {
    return this.saldoExibido(item) > 1;
  }

  removeQuantidade(item: ItemDePedidoConsultaDTO): void {
    if (!this.podeRemoverQuantidade(item)) return;

    // Desfaz primeiro o que ainda nao foi para o servidor.
    const pendente = this.incrementoPendente(item);
    if (pendente > 0 && item.id != null) {
      if (pendente === 1) {
        this.incrementosPendentes.delete(item.id);
      } else {
        this.incrementosPendentes.set(item.id, pendente - 1);
      }
      this.cdr.markForCheck();
      return;
    }

    item.saldo -= 1;
    this.reconciliarItemLocal(item);
    this.agendarPersistencia(item);
  }

  /** Atualiza quantidade e valor do item localmente (feedback imediato). */
  private reconciliarItemLocal(item: ItemDePedidoConsultaDTO): void {
    item.quantidade = item.saldo + (item.quantidadeFaturada || 0) + (item.quantidadeCancelada || 0);
    item.valorTotal = item.quantidade * item.valorUnitario;
    this.cdr.markForCheck();
  }

  /** Agenda a persistencia da quantidade agrupando cliques rapidos (~500ms). */
  private agendarPersistencia(item: ItemDePedidoConsultaDTO): void {
    if (item.id == null) return;
    const id = item.id;
    clearTimeout(this.persistTimers.get(id));
    const quantidade = item.quantidade;
    this.persistTimers.set(id, setTimeout(() => {
      this.persistTimers.delete(id);
      this.alterarQuantidadeItem.emit({ itemDePedidoId: id, quantidade });
    }, 500));
  }

  // ============================================================
  // Adicionar item (grade de produtos)
  // ============================================================

  abrirAdicionarItem(): void {
    this.adicionarItemVisivel = true;
  }

  onAdicionarItemVisivelChange(visivel: boolean): void {
    this.adicionarItemVisivel = visivel;
  }

  onCancelarPedido(): void {
    if (!this.pedido) return;
    this.cancelarPedido.emit(this.pedido);
  }

  onAbrirWhatsApp(): void {
    if (!this.pedido) return;
    this.abrirWhatsApp.emit(this.pedido);
  }

  /**
   * Emite o evento de compartilhar pedido.
   * O componente pai monta a mensagem (com dados do pedido) e abre o WhatsApp
   * para o dono do negocio encaminhar ao entregador/motoboy.
   */
  onCompartilharPedido(): void {
    if (!this.pedido) return;
    this.compartilharPedido.emit(this.pedido);
  }

  // ============================================================
  // Cancelar item
  // ============================================================

  abrirDialogCancelarItem(item: ItemDePedidoConsultaDTO): void {
    this.itemParaCancelar = item;
    this.quantidadeCancelar = 1;
    this.dialogCancelarItemVisivel = true;
  }

  // ============================================================
  // Totais dos itens
  // ============================================================

  /**
   * Quantidade de itens que o pedido tem AGORA: soma a coluna QUANTIDADE como
   * ela esta na tela, ou seja, incluindo os acrescimos e as linhas que ainda
   * nao foram confirmados. Os outros totais desta faixa vem do servidor e
   * ignoram o pendente — aqui nao, porque a pergunta e "quanto tem agora".
   */
  isColunaVisivel(key: string): boolean {
    return this.colunasItens.find(c => c.key === key)?.visible ?? true;
  }

  onColunasChange(colunas: TableColumn[]): void {
    this.colunasItens = [...colunas];
    this.cdr.markForCheck();
  }

  getTotalQuantidade(): number {
    const confirmados = (this.pedido?.itens || [])
      .reduce((acc, item) => acc + this.quantidadeExibida(item), 0);
    const pendentes = this.pendenciasDeAdicao
      .reduce((acc, p) => acc + (p.quantidade || 0), 0);
    return confirmados + pendentes;
  }

  getTotalSaldo(): number {
    if (!this.pedido?.itens) return 0;
    return this.pedido.itens.reduce((acc, item) => acc + (item.saldo || 0), 0);
  }

  getTotalCancelado(): number {
    if (!this.pedido?.itens) return 0;
    return this.pedido.itens.reduce((acc, item) => acc + (item.quantidadeCancelada || 0), 0);
  }

  getTotalFaturado(): number {
    if (!this.pedido?.itens) return 0;
    return this.pedido.itens.reduce((acc, item) => acc + (item.quantidadeFaturada || 0), 0);
  }

  // ============================================================
  // Formatacao de variantes e modificadores
  // ============================================================

  formatarVariantes(variantes: VarianteDePrecoDeItemPedidoDTO[], quantidadeDoItem?: number): string {
    if (!variantes || variantes.length === 0) return '';
    return variantes
      .map(v => {
        const nome = v.descricao || v.codigoDaVariante || '-';
        const porUnidade = this.quantidadePorUnidade(v.quantidade, quantidadeDoItem);
        return porUnidade > 1 ? `${nome} (x${porUnidade})` : nome;
      })
      .join(', ');
  }

  formatarModificadores(modificadores: ModificadorDePrecoDeItemPedidoDTO[], quantidadeDoItem?: number): string {
    if (!modificadores || modificadores.length === 0) return '';
    return modificadores
      .map(m => {
        const nome = m.descricao || m.codigoDoModificador || '-';
        const porUnidade = this.quantidadePorUnidade(m.quantidade, quantidadeDoItem);
        return porUnidade > 1 ? `${nome} (x${porUnidade})` : nome;
      })
      .join(', ');
  }

  /**
   * A quantidade da variante/modificador guardada no pedido e o TOTAL da linha (por unidade x
   * quantidade do item). O "(xN)" mostra so o que vai em CADA unidade — a quantidade do item ja
   * aparece na linha. Sem `quantidadeDoItem` (item ainda nao confirmado), ja e por unidade.
   */
  private quantidadePorUnidade(quantidade?: number, quantidadeDoItem?: number): number {
    if (!quantidade) return 0;
    return quantidadeDoItem ? Math.round(quantidade / quantidadeDoItem) : quantidade;
  }

  confirmarCancelarItem(): void {
    if (!this.itemParaCancelar?.id) return;

    const dto: CancelarItemPedidoDTO = {
      itemDePedidoId: this.itemParaCancelar.id,
      quantidade: this.quantidadeCancelar,
    };

    this.cancelarItem.emit(dto);
    this.dialogCancelarItemVisivel = false;
    this.itemParaCancelar = null;
  }
}
